import React, { createContext, useCallback, useEffect, useReducer, useState } from 'react';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import Icon from 'components/Utils/NewIcon/index';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import DefaultChip from 'components/Utils/DefaultChip';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultModal from 'components/Utils/DefaultModal';
import PackYearCalculatorModal from 'components/Modals/PackYearCalculatorModal/index';
import PregnancyCalculatorModal from 'components/Modals/PregnancyCalculatorModal';
import IVFCalculatorModal from 'components/Modals/IVFCalculatorModal';
import FRSCalculatorModal from 'components/Modals/FRSCalculatorModal';
import PREVENTCalculatorModal from 'components/Modals/PREVENTCalculatorModal';
import FIB4CalculatorModal from 'components/Modals/FIB4CalculatorModal';
import CPPPatientMedicationInput from 'components/Patient/CPP/CPPInput/Inputs/CPPPatientMedicationInput';
import AlcoholCalculatorModal from 'components/Modals/AlcoholCalculatorModal';
import StopBangCalculatorModal from 'components/Modals/StopBangCalculatorModal';
import BMICalculatorModal from 'components/Modals/BMICalculatorModal';
import IconButton from '@material-ui/core/IconButton';
import {
  HISTORY_TYPES,
  METRICS,
  OTHER_HEALTH_RECORDS,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import { run } from 'services/FunctionUtils';
import AIUtils from 'services/AIUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { uiActions } from 'careand-redux/slices/ui/ui';

export const NOTE_SECTIONS = [
  'allergies',
  'active_issues',
  'medical_issues',
  'surgical_history',
  'medications',
  'ethnicity_history',
  'family_history',
  'preventative_screenings',
  'lifestyle_factors',
];

const AppointmentNoteSections = ({
  sections_key,
  patient,
  health_records,
  onChange,
  valueRef,
  patientHr,
  processedMetrics,
}) => {
  const dispatch = useAppDispatch();
  const [metricsKey, setMetricsKey] = useState(1);

  const [isLoading, setLoadingAutofill] = useReducer(
    (state, { id, loading }) => {
      return { ...state, [id]: loading };
    },
    { metrics: false, allergies: false, patient_issues: false },
  );

  const patientId = patient?.id;

  // State for metrics view toggle
  const [metricsView, setMetricsView] = useState(() => {
    // Auto-detect if we should show multiple measurements view
    const hasMultipleMetrics = Object.values(processedMetrics || {}).some(
      (value) => Array.isArray(value) && value.length > 1,
    );
    return hasMultipleMetrics ? 'multiple' : 'quick';
  });

  const toggleMetricsView = () => {
    setMetricsView((prev) => (prev === 'quick' ? 'multiple' : 'quick'));
  };

  const onClickAutofill = useCallback(
    (id) => {
      run(async () => {
        setLoadingAutofill({ id, loading: true });
        if (id === 'metrics') {
          try {
            const metrics = await AIUtils.getAutofilledMetrics(patientId, valueRef.current);
            // Group metrics by name to handle multiple values
            const groupedMetrics = {};
            metrics.forEach((entry) => {
              if (entry.value !== undefined && entry.value !== null && entry.value !== '') {
                const metricName = entry.name;
                if (groupedMetrics[metricName]) {
                  if (Array.isArray(groupedMetrics[metricName])) {
                    groupedMetrics[metricName].push(entry.value);
                  } else {
                    groupedMetrics[metricName] = [groupedMetrics[metricName], entry.value];
                  }
                } else {
                  groupedMetrics[metricName] = entry.value;
                }
              }
            });
            // Apply the grouped metrics
            Object.entries(groupedMetrics).forEach(([name, value]) => {
              onChange(`processedMetrics.${name}`, value);
            });
          } finally {
            setMetricsKey((prevState) => prevState + 1);
          }
        } else {
          let result = [];

          if (id === 'allergies') {
            result = await AIUtils.getAutofilledAllergies(patientId, valueRef.current, patientHr);
          } else if (id === 'active_issues') {
            result = await AIUtils.getAutofilledActiveIssues(
              patientId,
              valueRef.current,
              patientHr,
            );
          } else if (id === 'medical_issues') {
            result = await AIUtils.getAutofilledMedicalIssues(
              patientId,
              valueRef.current,
              patientHr,
            );
          } else if (id === 'lifestyle_factors') {
            result = await AIUtils.getAutofilledLifestyle(patientId, valueRef.current, patientHr);
          } else if (id === 'medications') {
            result = await AIUtils.getAutofilledMedications(patientId, valueRef.current, patientHr);
          } else if (id === 'preventative_screenings') {
            result = await AIUtils.getPrefilledPreventativeScreenings(patientId, valueRef.current);
          } else if (Object.keys(HISTORY_TYPES).includes(id)) {
            result = await AIUtils.getAutofilledHistory(
              patientId,
              valueRef.current,
              patientHr,
              HISTORY_TYPES[id],
            );
          }

          document.dispatchEvent(
            new CustomEvent('appointmentNoteStackUpdated', {
              detail: { id, stack: result },
            }),
          );
        }
        setLoadingAutofill({ id, loading: false });
      }, []);
    },
    [dispatch, patientHr, patientId],
  );

  return (
    <div className="row sections" key={sections_key}>
      <UncontrolledCollapse
        titleElement={
          <span className="title-wrapper">
            <Icon className="title-icon" src="chart-line" height="15px" />
            Metrics
            <AutoFillButton
              loading={isLoading.metrics}
              onClick={() => onClickAutofill('metrics')}
              alt="Extract the metrics values from the note automatically"
            />
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleMetricsView();
              }}
              className="metric-view"
              style={{
                display: 'inline-flex',
                alignSelf: 'flex-end',
              }}
            >
              <Icon
                src={metricsView === 'quick' ? 'layer-group' : 'grip-horizontal'}
                alt={
                  metricsView === 'quick'
                    ? 'Switch to Multiple Measurements'
                    : 'Switch to Quick Entry'
                }
                showTooltip={true}
                width="15px"
              />
            </IconButton>
          </span>
        }
        initialValue={false}
      >
        <MetricsPanel
          key={metricsKey}
          processedMetrics={processedMetrics}
          onChange={onChange}
          metricsView={metricsView}
        />
      </UncontrolledCollapse>
      <UncontrolledCollapse
        titleElement={
          <span className="title-wrapper">
            <Icon className="title-icon" src="calculator" height="15px" /> Calculators
          </span>
        }
        initialValue={true}
      >
        <CalculatorsPanel patient={patient} />
      </UncontrolledCollapse>

      {OTHER_HEALTH_RECORDS.map((healthrecord) => (
        <StackForm
          key={healthrecord.id + healthrecord.name}
          healthrecord={healthrecord}
          health_records={health_records}
          onChangeForm={onChange}
          patient={patient}
          onClickAutofill={onClickAutofill}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
};

const DEFAULT_ARRAY = [];
const DEFAULT_OBJECT = {};

export const StackForm = ({
  healthrecord,
  health_records,
  onChangeForm,
  patient,
  lite = false,
  onClickAutofill,
  isLoading,
}) => {
  let {
    fields = ['name', 'notes'],
    name,
    id,
    autocomplete = DEFAULT_OBJECT,
    select = DEFAULT_OBJECT,
    hide,
  } = healthrecord;

  const [medicationModal, setMedicationModal] = useState(false);

  const savedVersion = health_records[id] || DEFAULT_ARRAY;
  let defaultStack = savedVersion.length > 0 ? [...savedVersion, getNewItem()] : [getNewItem()];

  const [stack, setStackValue] = useState(defaultStack);
  const [packYearModal, setPackYearModal] = useState(false);

  useEffect(() => {
    const cb = (event) => {
      if (id === event.detail.id) {
        setStack([...event.detail.stack, getNewItem()]);
      }
    };
    document.addEventListener('appointmentNoteStackUpdated', cb);

    return () => {
      document.removeEventListener('appointmentNoteStackUpdated', cb);
    };
  });

  if (hide && hide(patient)) {
    return null;
  }

  function getNewItem() {
    let item = { value: {} };
    fields.forEach((field) => (item.value[field] = ''));
    return item;
  }

  function setStack(value) {
    setStackValue(value);
    onChangeForm(`health_records.${id}`, PerformanceUtils.clone(filterEmpty(value)));
  }

  function onChange(event, index) {
    let newStack = PerformanceUtils.clone(stack);
    newStack[index].value[event.target.name] = event.target.value;
    Object.keys(healthrecord.values || {}).forEach((field) => {
      newStack[index].value[field] = healthrecord.values[field];
    });
    if (needsAnotherRow(newStack)) newStack = [...newStack, getNewItem()];

    //if (event.target.name === 'history_type') newStack[index].value.name = '';

    setStack(newStack);
  }

  function onChangeGTPAL(event, letter) {
    let gtpal = PerformanceUtils.clone(health_records[`${id}_main`] || {});
    gtpal[letter] = event.target.value;
    onChangeForm(`health_records.${id}_main`, gtpal);
  }

  function deleteItem(index) {
    let newStack = (stack || []).filter((item, i) => i !== index);
    if (needsAnotherRow(newStack)) newStack = [...newStack, getNewItem()];
    setStack(newStack);
  }

  function filterEmpty(stack = []) {
    return stack.filter((item) => !!(item.value || {}).name);
  }

  function needsAnotherRow(stack) {
    return !!stack[stack.length - 1].value.name;
  }

  function getLabel(field = '') {
    return field
      .split('_')
      .map((word) => PerformanceUtils.capitalizeFirstLetter(word))
      .join(' ');
  }

  function addToStack(value) {
    let newStack = [...stack];
    newStack[newStack.length - 1] = value;
    setStack([...newStack, getNewItem()]);
  }

  let title = name;
  let itemsLength = savedVersion.length;
  let titleElement = !lite ? (
    <span className="title-wrapper">
      <Icon src={healthrecord.icon} height="15px" className="title-icon" />
      {title}
      {itemsLength > 0 && <DefaultChip text={'' + itemsLength} disabled={true} />}
      {NOTE_SECTIONS.includes(id) && (
        <AutoFillButton
          onClick={() => onClickAutofill(id)}
          loading={isLoading[id]}
          alt={`Extract ${id.split('_').join(' ')} from the note automatically`}
        />
      )}
    </span>
  ) : null;

  let formClassName = `section-form hr-${healthrecord.id}`;

  if (healthrecord.hideOnEmpty && !health_records[id]?.length) return;

  return (
    <UncontrolledCollapse titleElement={titleElement} initialValue={false} key={id + name}>
      <form className={formClassName}>
        {id === 'gtpal' && (
          <div className="row">
            {['g', 't', 'p', 'a', 'l'].map((field) => (
              <DefaultTextField
                key={field}
                className="gtpal"
                name={field.toUpperCase()}
                placeholder={field.toUpperCase()}
                value={(health_records[`${id}_main`] || {})[field]}
                onChange={(event) => onChangeGTPAL(event, field)}
                style={{ width: '50px', marginRight: '20px' }}
              />
            ))}
          </div>
        )}
        {stack.map((item, index) => {
          let showPackYearButton = false;

          if (id === 'lifestyle_factors' && item.value.name === 'Smoking')
            showPackYearButton = true;

          return (
            <div className="row" key={index}>
              {fields.map((field, fieldIndex) => {
                let label = getLabel(field);
                let currentAutoComplete = autocomplete[field];

                if (healthrecord.detailsLabel && field === 'name') {
                  let realField = healthrecord.detailsLabel;
                  if (healthrecord.detailsLabelFn)
                    realField = healthrecord.detailsLabel(item.value.history_type);
                  if (field !== realField) {
                    label = getLabel(realField);
                    currentAutoComplete = autocomplete[realField];
                  }
                }

                const className = healthrecord.id + '-' + field;
                const elementId = healthrecord.id + '-' + field + '-' + fieldIndex + '-' + index;

                const hasSelect =
                  !!(select.inputs || {})[field] || !!(select.inputElements || {})[field];

                const hidden = healthrecord.values?.[field];

                return (
                  <React.Fragment key={field}>
                    {!currentAutoComplete && hasSelect && (
                      <DefaultSelectField
                        disabled={healthrecord.values?.[field]}
                        id={elementId}
                        className={className}
                        placeholder={label}
                        value={healthrecord.values?.[field] || item.value[field]}
                        onChange={(event) => {
                          let newEvent = {
                            target: { name: field, value: event.target.value },
                          };
                          onChange(newEvent, index);
                        }}
                        inputs={
                          (select.inputs || {})[field]
                            ? Object.values((select.inputs || {})[field])
                            : undefined
                        }
                        inputElements={(select.inputElements || {})[field]}
                        style={{
                          width: '200px',
                          marginRight: '20px',
                          display: hidden ? 'none' : 'unset',
                        }}
                      />
                    )}
                    {!!currentAutoComplete && !hasSelect && (
                      <DefaultAutoComplete
                        disabled={healthrecord.values?.[field]}
                        id={elementId}
                        className={className}
                        name={field}
                        placeholder={label}
                        input={{
                          value: item.value[field],
                          onChange: (value) => onChange({ target: { name: field, value } }, index),
                        }}
                        onUpdateInput={(value) => {
                          onChange({ target: { name: field, value } }, index);
                        }}
                        dataSource={currentAutoComplete}
                        style={{ display: hidden ? 'none' : 'unset' }}
                      />
                    )}
                    {!currentAutoComplete && !hasSelect && (
                      <DefaultTextField
                        disabled={healthrecord.values?.[field]}
                        id={elementId}
                        className={className}
                        name={field}
                        placeholder={label}
                        value={healthrecord.values?.[field] || item.value[field]}
                        onChange={(event) => onChange(event, index)}
                        style={{
                          width: '200px',
                          marginRight: '20px',
                          display: hidden ? 'none' : 'unset',
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
              {id === 'medications' && index === stack.length - 1 && !lite && (
                <div
                  style={{
                    display: 'flex',
                    alignSelf: 'flex-end',
                  }}
                >
                  <DefaultButton
                    icon="pills"
                    name="Add from DrugBank"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setMedicationModal(true);
                    }}
                    style={{ width: '200px' }}
                  />
                </div>
              )}
              {index !== stack.length - 1 && (
                <>
                  <Icon
                    src="circle-minus"
                    width="20px"
                    style={{ margin: 0, alignSelf: 'flex-end' }}
                    onClick={() => deleteItem(index)}
                  />
                  {showPackYearButton && (
                    <>
                      <DefaultButton
                        name="Calculate Pack-Year"
                        onClick={(event) => {
                          event.preventDefault();
                          setPackYearModal(true);
                        }}
                        style={{
                          width: '180px',
                          height: '35px',
                          marginLeft: '20px',
                          marginTop: '3px',
                        }}
                      />
                      {packYearModal && (
                        <DefaultModal
                          key="pack-year-modal"
                          content={
                            <PackYearCalculatorModal
                              onSubmit={(value) => {
                                let event = {
                                  target: {
                                    name: 'details',
                                    value: `${value} pack-year`,
                                  },
                                };
                                setPackYearModal(false);
                                onChange(event, index);
                              }}
                            />
                          }
                          isOpen={packYearModal}
                          onRequestClose={() => setPackYearModal(false)}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}
      </form>
      {!!medicationModal && (
        <DefaultModal
          className="add-medication-modal"
          title="Add Medication"
          wrap={true}
          padding={true}
          draggable
          content={
            <CPPPatientMedicationInput
              key="medications"
              title="Medication"
              field="medications"
              onClose={() => setMedicationModal(false)}
              onSave={(medication) => {
                setMedicationModal(false);
                addToStack(medication);
              }}
              patient={patient}
            />
          }
          isOpen={!!medicationModal}
          onRequestClose={() => setMedicationModal(false)}
        />
      )}
    </UncontrolledCollapse>
  );
};

export const AutofillNoteSectionsContext = createContext({
  isLoading: {
    metrics: false,
    allergies: false,
    medical_issues: false,
  },
  onClickAutofill: (id) => {},
});

const MetricsPanel = ({ processedMetrics, onChange, metricsView }) => {
  if (metricsView === 'multiple') {
    return (
      <div className="metrics-multiple">
        <MetricsStackForm processedMetrics={processedMetrics} onChange={onChange} />
      </div>
    );
  }

  // Helper to get the display value for quick entry (first value if array)
  const getDisplayValue = (metricKey) => {
    const value = processedMetrics?.[metricKey];
    return Array.isArray(value) ? value[0] || '' : value || '';
  };

  // Helper to handle value changes in quick entry mode
  const handleQuickEntryChange = (metricKey, newValue) => {
    const currentValue = processedMetrics?.[metricKey];
    if (Array.isArray(currentValue) && currentValue.length > 1) {
      // Update only the first value, keep the rest
      const updatedArray = [newValue, ...currentValue.slice(1)];
      onChange(`processedMetrics.${metricKey}`, updatedArray);
    } else {
      // Single value
      onChange(`processedMetrics.${metricKey}`, newValue);
    }
  };

  return (
    <div className="metrics">
      {METRICS.map((metric) => {
        let width = metric.width || '50px';
        const metricValue = processedMetrics?.[metric.metric];
        const hasMultiple = Array.isArray(metricValue) && metricValue.length > 1;
        const displayValue = getDisplayValue(metric.metric);
        const additionalCount = hasMultiple ? metricValue.length - 1 : 0;

        return (
          <DefaultTooltip
            key={metric.metric}
            placement="top"
            title={
              hasMultiple
                ? `${metric.name}: ${displayValue}, +${additionalCount} more (${metricValue
                    .slice(1)
                    .join(', ')})`
                : metric.name
            }
          >
            <div className="simple-metric" key={metric.metric}>
              <DefaultTextField
                label={metric.label}
                value={displayValue}
                onChange={(e) => handleQuickEntryChange(metric.metric, e.target.value)}
                disabled={metric.disabled}
                style={{ width, marginLeft: '10px' }}
              />
              {hasMultiple && <span className="additional-count">+{additionalCount}</span>}
            </div>
          </DefaultTooltip>
        );
      })}
    </div>
  );
};

const MetricsStackForm = ({ processedMetrics, onChange }) => {
  // Convert processedMetrics to a flat array of {metricType, value} objects
  const getInitialStack = () => {
    const stack = [];
    Object.entries(processedMetrics || {}).forEach(([metricKey, metricValue]) => {
      if (metricValue) {
        if (Array.isArray(metricValue)) {
          metricValue.forEach((val) => {
            if (val && val.trim && val.trim() !== '') {
              stack.push({ metricType: metricKey, value: val });
            }
          });
        } else if (metricValue.trim && metricValue.trim() !== '') {
          stack.push({ metricType: metricKey, value: metricValue });
        }
      }
    });
    // Add empty row at the end
    stack.push({ metricType: '', value: '' });
    return stack;
  };

  const [stack, setStackValue] = useState(getInitialStack);

  function setStack(newStack) {
    setStackValue(newStack);

    // Convert stack back to processedMetrics format
    const newProcessedMetrics = {};
    newStack.forEach((item) => {
      if (item.metricType && item.value && item.value.trim && item.value.trim() !== '') {
        if (newProcessedMetrics[item.metricType]) {
          if (Array.isArray(newProcessedMetrics[item.metricType])) {
            newProcessedMetrics[item.metricType].push(item.value);
          } else {
            newProcessedMetrics[item.metricType] = [
              newProcessedMetrics[item.metricType],
              item.value,
            ];
          }
        } else {
          newProcessedMetrics[item.metricType] = item.value;
        }
      }
    });
    onChange(`processedMetrics`, newProcessedMetrics);
  }

  function onChangeMetricType(value, index) {
    let newStack = [...stack];
    newStack[index].metricType = value;

    if (
      index === newStack.length - 1 &&
      newStack[index].metricType &&
      newStack[index].value.trim() !== ''
    ) {
      newStack = [...newStack, { metricType: '', value: '' }];
    }

    setStack(newStack);
  }

  function onChangeValue(event, index) {
    let newStack = [...stack];
    newStack[index].value = event.target.value;

    // Add another empty row if both fields are filled on the last row
    if (
      index === newStack.length - 1 &&
      newStack[index].metricType &&
      event.target.value.trim() !== ''
    ) {
      newStack = [...newStack, { metricType: '', value: '' }];
    }

    setStack(newStack);
  }

  function deleteItem(index) {
    let newStack = stack.filter((_, i) => i !== index);
    // Ensure there's always at least one empty row
    if (
      newStack.length === 0 ||
      newStack[newStack.length - 1].value.trim() !== '' ||
      newStack[newStack.length - 1].metricType !== ''
    ) {
      newStack = [...newStack, { metricType: '', value: '' }];
    }
    setStack(newStack);
  }

  return (
    <div className="section-form form-metrics">
      {stack.map((item, index) => {
        const isLastRow = index === stack.length - 1;
        const selectedMetric = METRICS.find((m) => m.metric === item.metricType);
        const isBMI = selectedMetric?.metric === 'bmi';

        if (isBMI) return null;

        return (
          <div
            className="row"
            key={(selectedMetric?.metric || 'metric') + index}
            style={{ marginBottom: '5px' }}
          >
            <DefaultAutoComplete
              id={`metric-type-${index}`}
              placeholder="Select Metric"
              input={{
                value: selectedMetric?.label || '',
                onChange: (value) => {
                  const metric = METRICS.find((m) => m.label === value);
                  if (metric) onChangeMetricType(metric.metric, index);
                },
              }}
              dataSource={METRICS.map((m) => m.label)}
              popOut={false}
            />
            <DefaultTextField
              name="metric-value"
              placeholder={selectedMetric?.label || 'Value'}
              value={item.value}
              onChange={(event) => onChangeValue(event, index)}
              disabled={!item.metricType}
              style={{ width: '200px', marginLeft: '10px', marginRight: '10px' }}
            />
            {!isLastRow && (
              <Icon
                src="circle-minus"
                width="20px"
                style={{ margin: 0, alignSelf: 'flex-end', cursor: 'pointer' }}
                onClick={() => deleteItem(index)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const AutoFillButton = ({ loading, onClick, alt }) => {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignSelf: 'flex-end',
      }}
    >
      <IconButton
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!loading) onClick();
        }}
        className="autofill-button blue"
        size="small"
        style={{ width: '30px', height: '30px' }}
      >
        <Icon
          loading={loading}
          src="microchip-ai"
          className="white"
          showTooltip={true}
          width="15px"
          alt={alt}
        />
      </IconButton>
    </div>
  );
};

const CalculatorsPanel = ({ patient }) => (
  <div className="calculators">
    <Calculator
      type="bmi"
      name="BMI"
      patient={patient}
      calculator={<BMICalculatorModal patient={patient} />}
    />
    <Calculator
      type="pregnancy"
      name="Pregnancy"
      patient={patient}
      calculator={<PregnancyCalculatorModal patient={patient} />}
    />
    <Calculator
      type="ivf"
      name="IVF"
      patient={patient}
      calculator={<IVFCalculatorModal patient={patient} />}
      style={{ width: '400px' }}
    />
    <Calculator
      type="frs"
      name="FRS"
      patient={patient}
      calculator={<FRSCalculatorModal patient={patient} />}
    />
    <Calculator
      type="prevent"
      name="PREVENT"
      patient={patient}
      calculator={<PREVENTCalculatorModal patient={patient} />}
    />
    <Calculator
      type="fib4"
      name="FIB-4"
      patient={patient}
      calculator={<FIB4CalculatorModal patient={patient} />}
    />
    <Calculator
      type="alcohol"
      name="Alcohol"
      patient={patient}
      calculator={<AlcoholCalculatorModal patient={patient} />}
      style={{ width: '600px' }}
    />
    <Calculator
      type="stop-bang"
      name="Stop-Bang"
      patient={patient}
      calculator={<StopBangCalculatorModal patient={patient} />}
      style={{ width: '600px' }}
    />
  </div>
);

export const CalculatorModal = ({ calculator, name, type, patient, open, style, setOpen }) => {
  const dispatch = useAppDispatch();

  return (
    <DefaultModal
      content={
        <>
          <IconButton
            onClick={() => {
              dispatch(
                uiActions.addPinnedDocument({
                  id: type,
                  type: 'calculator',
                  patientId: patient.id,
                  calculatorType: type,
                  title: name,
                }),
              );
              setOpen(false);
            }}
            style={{
              position: 'absolute',
              right: '15px',
              top: '2px',
              zIndex: 1,
            }}
          >
            <Icon src="thumbtack" alt="Pin to Side" showTooltip={true} width="20px" height="20px" />
          </IconButton>
          {calculator}
        </>
      }
      isOpen={open}
      onRequestClose={() => setOpen(false)}
      style={style}
      draggable
    />
  );
};

export const Calculator = ({
  name,
  type,
  icon,
  calculator,
  style,
  patient,
  buttonStyle = {},
  simpleButton = false,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {open && (
        <CalculatorModal
          calculator={calculator}
          name={name}
          type={type}
          patient={patient}
          style={style}
          open={open}
          setOpen={setOpen}
        />
      )}
      {simpleButton ? (
        <span className="full-width" onClick={() => setOpen(!open)}>
          {name}
        </span>
      ) : (
        <DefaultButton
          name={name}
          icon={icon}
          onClick={() => setOpen(!open)}
          style={{ marginTop: '15px', marginRight: '15px', ...buttonStyle }}
        />
      )}
    </>
  );
};

export default React.memo(AppointmentNoteSections);
